import { useState, useEffect, useRef } from "react";
import type { ServiceStatus } from "@/hooks/useStatus";

interface Props {
  service: string;
  status: ServiceStatus;
  anchorRect: DOMRect;
  onClose: () => void;
}

interface ErrorEntry {
  timestamp: string;
  message: string;
}

function stateColor(state: string) {
  if (state === "green") return "#00b894";
  if (state === "yellow") return "#fdcb6e";
  return "#e17055";
}

export default function StatusDrawer({ service, status, anchorRect, onClose }: Props) {
  const [errors, setErrors] = useState<ErrorEntry[]>([]);
  const [errorsLoading, setErrorsLoading] = useState(true);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchErrors = async () => {
      const token = localStorage.getItem("bazaar_token");
      try {
        const res = await fetch(
          `/api/v1/status/errors?service=${encodeURIComponent(service)}&limit=5`,
          {
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
        if (res.ok) {
          const data = await res.json();
          setErrors(Array.isArray(data) ? data : data.errors || []);
        }
      } catch {
        // silently ignore
      } finally {
        setErrorsLoading(false);
      }
    };
    fetchErrors();
  }, [service]);

  const label = service.charAt(0).toUpperCase() + service.slice(1);

  const left = Math.min(anchorRect.left, window.innerWidth - 320);
  const top = anchorRect.bottom + 8;

  return (
    <div
      ref={panelRef}
      style={{
        position: "fixed",
        top,
        left,
        width: 300,
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 8,
        boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
        zIndex: 1000,
        padding: 16,
        fontFamily: "inherit",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: stateColor(status.state),
              display: "inline-block",
            }}
          />
          <span style={{ fontWeight: 600, fontSize: 14 }}>{label}</span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              textTransform: "uppercase",
              padding: "2px 6px",
              borderRadius: 4,
              background: stateColor(status.state) + "22",
              color: stateColor(status.state),
            }}
          >
            {status.state}
          </span>
        </div>
        <button
          onClick={onClose}
          style={{
            background: "transparent",
            border: "none",
            color: "var(--text-muted)",
            fontSize: 16,
            cursor: "pointer",
            padding: "0 4px",
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>

      {/* Message */}
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
        {status.message}
      </div>

      {/* Errors */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--text-muted)", marginBottom: 6 }}>
          Recent Errors
        </div>
        {errorsLoading ? (
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Loading...</div>
        ) : errors.length === 0 ? (
          <div style={{ fontSize: 12, color: "#00b894" }}>No recent errors</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {errors.map((err, i) => (
              <div
                key={i}
                style={{
                  fontSize: 11,
                  padding: "6px 8px",
                  background: "var(--bg)",
                  borderRadius: 4,
                  borderLeft: "2px solid #e17055",
                }}
              >
                <div style={{ color: "var(--text-muted)", marginBottom: 2 }}>
                  {err.timestamp ? new Date(err.timestamp).toLocaleString() : ""}
                </div>
                <div style={{ color: "var(--text)", wordBreak: "break-word" }}>
                  {err.message}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action button */}
      {status.action && (
        <button
          style={{
            width: "100%",
            padding: "8px 12px",
            background: "var(--accent)",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 600,
          }}
          onClick={() => {
            if (status.action === "open_settings") {
              window.location.hash = "/settings";
            }
            onClose();
          }}
        >
          {status.action === "open_settings" ? "Open Settings →" : status.action}
        </button>
      )}
    </div>
  );
}
